import { Injectable } from '@angular/core';
import { Result } from '@gametailors/web-core/classes';
import { lastValueFrom } from 'rxjs';
import { BaseAPI, BaseRequestOptions } from './base-api';
import { EndpointError } from './errors';

export type GenerateErrorCode = 'UNKNOWN' | 'GENERATOR_FAILED';

/**
 * Not provided in root: the game injects it optionally and generates content locally where no app provides a concrete
 * subclass for it, for instance when it is played offline.
 */
@Injectable()
export abstract class RecommendationApiService extends BaseAPI {
  /**
   * Runs the generator's output formula on the server. Without `forUser` it runs for the logged in user. The result is
   * whatever that formula returns, so it has no fixed shape. A formula that finds nothing to return fails with
   * GENERATOR_FAILED and its reason as the message.
   */
  public generate(
    generatorId: string,
    inputs: Record<string, unknown> = {},
    forUser?: string,
  ): Promise<Result<unknown, EndpointError<GenerateErrorCode>>> {
    return lastValueFrom(
      this.post<{ inputs: Record<string, unknown> }, unknown, GenerateErrorCode>(
        `/recommendations/generators/${encodeURIComponent(generatorId)}`,
        {
          ...this.requestOptions(),
          body: { inputs },
          params: forUser ? { forUser } : {},
        },
      ),
    );
  }

  /** Added to every request, for a subclass that authenticates differently. Asked for on each request. */
  protected requestOptions(): Pick<BaseRequestOptions, 'headers' | 'context'> {
    return {};
  }
}
